import { useT } from "@transifex/react";
import type { StudentQuestionAnswer, StudentTestExercise } from "p6m-p6u";
import { useState } from "react";
import PhaseSixIcon from "../../basic/phaseSixIcon/PhaseSixIcon";
import { ShareModes } from "../../enums/shareFlow";
import { getExerciseProgress } from "../../helpers/OnlineTestHelper";
import { formatScoreToCommaSeparated } from "../../helpers/ScoringHelper";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import ExerciseCard from "./exercises/ExerciseCard";
import OnlineTestLayout from "./layout/OnlineTestLayout";
import OnlineTestPopup from "./OnlineTestPopup";

interface ExerciseOverviewProps {
    exercises: StudentTestExercise[];
    questionAnswers: { [key: string]: StudentQuestionAnswer };
    timeRemaining?: string;
    isTimeRunningOut?: boolean;
    shouldBounceTimer?: boolean;
    onBounceEnd?: () => void;
    /** Kept visible for the whole test; absent for public classes and teacher previews. */
    studentName?: string;
    currentExerciseId?: string;
    currentExerciseNumber?: number;
    totalScore?: number;
    isPreview?: boolean;
    isOwner?: boolean;
    shareMode?: ShareModes | null;
    onClose: () => void;
    onNavigateToExercise: (exerciseId: string) => void;
    onSubmit: () => void;
}

const ExercisesOverview = (props: ExerciseOverviewProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint, isAboveMD } = useBreakpoints();

    const [isPopupOpen, setIsPopupOpen] = useState(false);

    const isPractice = props.shareMode === ShareModes.PRACTICE;

    const t_title = t("Overview", { _tags: "OnlineTest,ExerciseOverview" });
    const t_submit = t("Submit answers", { _tags: "OnlineTest,ExerciseOverview" });
    const t_endPractice = t("End practice", { _tags: "OnlineTest,ExerciseOverview" });
    const t_endPreview = t("End Preview", { _tags: "OnlineTest,ExerciseOverview" });
    const t_endPracticeTitle = t("Do you want to end this practice?", { _tags: "OnlineTest,ExerciseOverview" });
    const t_endPracticeRepeatable = t("You can repeat this practice as often as you want.", {
        _tags: "OnlineTest,ExerciseOverview",
    });
    const t_endPracticeNotSaved = t("Your answers will not be saved.", {
        _tags: "OnlineTest,ExerciseOverview",
    });
    const t_endPracticeConfirm = t("Yes, I am done.", { _tags: "OnlineTest,ExerciseOverview" });
    const t_back = t("Back", { _tags: "OnlineTest,ExerciseOverview" });
    const t_backToExercise = t("Back to exercise {number}", {
        _tags: "OnlineTest,ExerciseOverview",
        number: props.currentExerciseNumber ?? "",
    });
    const t_totalScore = t("Total score", { _tags: "OnlineTest,ExerciseOverview" });
    const t_totalScoreShort = t("Total", { _tags: "OnlineTest,ExerciseOverview" });
    const t_pointsAbbreviated = t("P.", { _tags: "OnlineTest,ExerciseOverview" });
    const t_popupTitle = t("Are you sure you want to submit your answers?", { _tags: "OnlineTest,ExerciseOverview" });
    const t_incompleteWarning = t("Not all exercises are fully completed.", { _tags: "OnlineTest,ExerciseOverview" });
    const t_noEditAfterSubmit = t("You can no longer edit your answers after you submit them.", {
        _tags: "OnlineTest,ExerciseOverview",
    });
    const t_cancel = t("Cancel", { _tags: "OnlineTest,ExerciseOverview" });
    const t_submitNow = t("Submit now", { _tags: "OnlineTest,ExerciseOverview" });
    const t_popupAriaLabel = t("Submit confirmation", { _tags: "OnlineTest,ExerciseOverview" });
    const t_previewDescription = t("This page shows a preview of your task. The answers are not saved.", {
        _tags: "OnlineTest,ExerciseOverview",
    });
    const t_backToPreview = t("back to preview", { _tags: "OnlineTest,ExerciseOverview" });
    const t_closePreview = t("Close", { _tags: "OnlineTest,ExerciseOverview" });

    const hasIncompleteExercises = props.exercises.some((exercise) => {
        const answer = exercise.id ? props.questionAnswers[exercise.id] : undefined;
        return getExerciseProgress(answer, exercise.questionMode) < 100;
    });

    // Two distinct preview-mode behaviours:
    //   - Owner viewing their own test via /student/:id → "End Preview" button, instant close.
    //   - Teacher author viewing /create/preview → keep original "Submit answers" / "End practice"
    //     labels; TEST/legacy keeps the preview confirmation popup, PRACTICE skips the popup
    //     because practice never submits in the first place, so a confirmation is pointless.
    const isOwner = !!props.isOwner;
    const isInCreatePreview = !!props.isPreview && !isOwner;
    const skipConfirmation = isOwner || (isInCreatePreview && isPractice);
    const getFooterButtonLabel = () => {
        if (isOwner) return t_endPreview;
        if (isPractice) return t_endPractice;
        return t_submit;
    };
    const handleFooterButtonClick = () => {
        if (skipConfirmation) {
            props.onSubmit();
        } else {
            setIsPopupOpen(true);
        }
    };

    const renderTotalScore = () => {
        if (props.totalScore === undefined) return null;

        const formattedScore = formatScoreToCommaSeparated(props.totalScore);

        if (isAboveMD) {
            return (
                <p className="text-l text-p6-grey-dark">
                    {t_totalScore}: <strong>{formattedScore}</strong>
                </p>
            );
        }

        return (
            <p className="text-l text-p6-grey-dark">
                {t_totalScoreShort}: <strong>{`${formattedScore} ${t_pointsAbbreviated}`}</strong>
            </p>
        );
    };

    const renderExerciseRow = (exercise: StudentTestExercise, index: number) => {
        const exerciseId = exercise.id;
        if (!exerciseId) return null;

        const answer = props.questionAnswers[exerciseId];
        const progress = getExerciseProgress(answer, exercise.questionMode);

        return (
            <ExerciseCard
                key={exerciseId}
                number={index + 1}
                title={exercise.extraComments ?? ""}
                progress={progress}
                onClick={() => props.onNavigateToExercise(exerciseId)}
                data-testid={`online-test-overview-card-${index + 1}`}
            />
        );
    };

    const renderFooter = () => (
        <div className="z-50 w-full bg-background-light pt-s pb-xl">
            <div
                className={`max-w-[1000px] mx-auto flex flex-row items-center justify-between ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "px-m",
                    "px-s"
                )}`}
            >
                {props.currentExerciseId && (
                    <button
                        className="flex flex-row items-center gap-xs bg-transparent border-none cursor-pointer"
                        onClick={() => props.onNavigateToExercise(props.currentExerciseId!)}
                        data-testid="online-test-back-button"
                    >
                        <PhaseSixIcon
                            name="chevron-left"
                            className="text-[30px] text-p6-grey-dark"
                        />
                        <span className="text-l text-p6-grey-dark">{isAboveMD ? t_backToExercise : t_back}</span>
                    </button>
                )}
                {renderTotalScore()}
            </div>
            <div className={`flex justify-center mt-s ${getStyleBasedOnBreakpoint(Breakpoint.MD, "", "px-s")}`}>
                <button
                    className={`bg-p6-orange text-white border border-p6-orange rounded-button text-m font-semibold flex flex-row items-center justify-center cursor-pointer hover:bg-p6-orange-brighter hover:border-p6-orange-brighter transition-colors duration-200 ${getStyleBasedOnBreakpoint(
                        Breakpoint.SM,
                        "px-xl py-xs",
                        "w-full py-xs"
                    )}`}
                    onClick={handleFooterButtonClick}
                    data-testid="online-test-submit-button"
                >
                    {getFooterButtonLabel()}
                </button>
            </div>
        </div>
    );

    const renderSubmitPopup = () => (
        <OnlineTestPopup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            ariaLabel={t_popupAriaLabel}
            data-testid="online-test-submit-dialog"
            primaryAction={{
                label: t_submitNow,
                accessibilityLabel: t_submitNow,
                onClick: () => {
                    setIsPopupOpen(false);
                    props.onSubmit();
                },
                "data-testid": "online-test-submit-confirm-button",
            }}
            secondaryAction={{
                label: t_cancel,
                accessibilityLabel: t_cancel,
                onClick: () => setIsPopupOpen(false),
                "data-testid": "online-test-submit-cancel-button",
            }}
        >
            <p className="text-xl text-p6-grey-dark">{t_popupTitle}</p>

            <div className="flex flex-col gap-xxs">
                {hasIncompleteExercises && <p className="text-l text-p6-grey-dark">{t_incompleteWarning}</p>}
                <p className="text-l text-p6-grey-dark">{t_noEditAfterSubmit}</p>
            </div>
        </OnlineTestPopup>
    );

    const renderEndPracticePopup = () => (
        <OnlineTestPopup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            ariaLabel={t_endPracticeTitle}
            data-testid="online-test-end-practice-dialog"
            primaryAction={{
                label: t_endPracticeConfirm,
                accessibilityLabel: t_endPracticeConfirm,
                onClick: () => {
                    setIsPopupOpen(false);
                    props.onSubmit();
                },
                "data-testid": "online-test-end-practice-confirm-button",
            }}
            secondaryAction={{
                label: t_cancel,
                accessibilityLabel: t_cancel,
                onClick: () => setIsPopupOpen(false),
                "data-testid": "online-test-end-practice-cancel-button",
            }}
        >
            <div className="flex flex-col gap-s">
                <p className="text-xl text-p6-grey-dark">{t_endPracticeTitle}</p>
                <div className="flex flex-col gap-xxs">
                    <p className="text-l text-p6-grey-dark">{t_endPracticeRepeatable}</p>
                    <p className="text-l text-p6-grey-dark">{t_endPracticeNotSaved}</p>
                </div>
            </div>
        </OnlineTestPopup>
    );

    const renderPreviewSubmitPopup = () => (
        <OnlineTestPopup
            isOpen={isPopupOpen}
            onClose={() => setIsPopupOpen(false)}
            ariaLabel={t_popupAriaLabel}
            data-testid="online-test-preview-submit-dialog"
            primaryAction={{
                label: t_closePreview,
                accessibilityLabel: t_closePreview,
                onClick: () => {
                    setIsPopupOpen(false);
                    props.onSubmit();
                },
                "data-testid": "online-test-preview-close-button",
            }}
            secondaryAction={{
                label: t_backToPreview,
                accessibilityLabel: t_backToPreview,
                onClick: () => setIsPopupOpen(false),
                "data-testid": "online-test-preview-back-button",
            }}
        >
            <p className="text-l text-p6-grey-dark">{t_previewDescription}</p>
        </OnlineTestPopup>
    );

    const renderActivePopup = () => {
        if (skipConfirmation) return null;
        if (isInCreatePreview) return renderPreviewSubmitPopup();
        if (isPractice) return renderEndPracticePopup();
        return renderSubmitPopup();
    };

    return (
        <>
            <OnlineTestLayout
                showHeader
                headerTimeRemaining={props.timeRemaining}
                isTimeRunningOut={props.isTimeRunningOut}
                shouldBounceTimer={props.shouldBounceTimer}
                onBounceEnd={props.onBounceEnd}
                headerStudentName={props.studentName}
                onClose={props.onClose}
                footer={renderFooter()}
            >
                <div className="flex flex-col gap-l w-full pb-l">
                    <p
                        className={`${getStyleBasedOnBreakpoint(
                            Breakpoint.MD,
                            "text-xxl",
                            "text-xl"
                        )} text-p6-grey-dark text-center`}
                    >
                        {t_title}
                    </p>
                    <div className="flex flex-col gap-s">{props.exercises.map(renderExerciseRow)}</div>
                </div>
            </OnlineTestLayout>
            {renderActivePopup()}
        </>
    );
};

export default ExercisesOverview;
